export type StorefrontFeatures = {
  wishlist: boolean;
  brands: boolean;
  hideEmptyCategories: boolean;
  catalogSortOptions: ("popularity" | "rating")[];
  priceFilterPresentation: "separate" | "inputs" | "range";
  priceFilterStepMinor: number;
  catalogDisplayMode: "products" | "subcategories" | "both";
  categoryCardCta: Record<string, string>;
  categoryCardExcerptWords: number;
  priceTaxDisplay: "single" | "both";
  quickOrderIntro: Record<string, string>;
  quickOrderSteps: Record<string, string[]>;
  quickOrderCategoryTiles: boolean;
  quickOrderRootCategoryIds: string[];
  detailVariants: "select" | "swatches";
  attributeFilterStyle: "select" | "checkboxes";
  catalogBreadcrumbs: boolean;
  categoryFilterScope: "direct" | "descendants";
  brandAttribute: string;
  inlineVariants: "none" | "select" | "swatches";
  initialVariant: "first" | "none";
  relatedProducts: boolean;
  categoryBreadcrumbs: boolean;
  attributeFilters: string[];
  catalogOrder: "newest" | "catalog";
  swatchColors: Record<string, string>;
  labels: Record<string, Record<string, string>>;
};

function recordValue(value: unknown): Record<string, unknown> {
  if (typeof value === "string" && value.length <= 200_000) {
    try { value = JSON.parse(value); } catch { return {}; }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function normalizeStorefrontFeatures(value: unknown): StorefrontFeatures {
  const data = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const localizedText = (value: unknown, max: number) => Object.fromEntries(Object.entries(recordValue(value)).slice(0, 20)
    .filter((entry): entry is [string, string] => /^[a-z]{2,3}(?:-[A-Za-z0-9]+)*$/.test(entry[0]) && typeof entry[1] === "string" && entry[1].length <= max));
  let roots = data.quickOrderRootCategoryIds;
  if (typeof roots === "string") { try { roots = JSON.parse(roots); } catch { roots = []; } }
  return {
    wishlist: data.wishlist === true,
    brands: data.brands === true,
    hideEmptyCategories: data.hideEmptyCategories === true,
    catalogSortOptions: [...new Set((Array.isArray(data.catalogSortOptions) ? data.catalogSortOptions : String(data.catalogSortOptions ?? "").split(",")).filter((v): v is "popularity" | "rating" => v === "popularity" || v === "rating"))],
    priceFilterPresentation: data.priceFilterPresentation === "range" || data.priceFilterPresentation === "inputs" ? data.priceFilterPresentation : "separate",
    priceFilterStepMinor: Number.isSafeInteger(Number(data.priceFilterStepMinor)) && Number(data.priceFilterStepMinor) >= 1 && Number(data.priceFilterStepMinor) <= 1_000_000 ? Number(data.priceFilterStepMinor) : 1,
    catalogDisplayMode: data.catalogDisplayMode === "subcategories" || data.catalogDisplayMode === "both" ? data.catalogDisplayMode : "products",
    categoryCardCta: localizedText(data.categoryCardCta, 500),
    categoryCardExcerptWords: Number.isSafeInteger(data.categoryCardExcerptWords) && Number(data.categoryCardExcerptWords) >= 0 && Number(data.categoryCardExcerptWords) <= 100 ? Number(data.categoryCardExcerptWords) : 0,
    priceTaxDisplay: data.priceTaxDisplay === "both" ? "both" : "single",
    quickOrderIntro: localizedText(data.quickOrderIntro, 4000),
    quickOrderSteps: Object.fromEntries(Object.entries(recordValue(data.quickOrderSteps)).slice(0, 20)
      .filter((entry): entry is [string, string[]] => /^[a-z]{2,3}(?:-[A-Za-z0-9]+)*$/.test(entry[0]) && Array.isArray(entry[1]) && entry[1].length <= 8 && entry[1].every(s => typeof s === "string" && s.length <= 300))),
    quickOrderCategoryTiles: data.quickOrderCategoryTiles === true,
    quickOrderRootCategoryIds: Array.isArray(roots) ? [...new Set(roots.filter((id): id is string => typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)).map(id => id.toLowerCase()))].slice(0, 100) : [],
    detailVariants: data.detailVariants === "swatches" ? "swatches" : "select",
    attributeFilterStyle: data.attributeFilterStyle === "checkboxes" ? "checkboxes" : "select",
    catalogBreadcrumbs: data.catalogBreadcrumbs === true,
    categoryFilterScope: data.categoryFilterScope === "descendants" ? "descendants" : "direct",
    brandAttribute: typeof data.brandAttribute === "string" ? data.brandAttribute.trim().slice(0, 160) : "",
    inlineVariants: data.inlineVariants === "select" || data.inlineVariants === "swatches" ? data.inlineVariants : "none",
    initialVariant: data.initialVariant === "none" ? "none" : "first",
    relatedProducts: data.relatedProducts === true,
    categoryBreadcrumbs: data.categoryBreadcrumbs === true,
    attributeFilters: [...new Set((Array.isArray(data.attributeFilters) ? data.attributeFilters : String(data.attributeFilters ?? "").split(","))
      .filter((item): item is string => typeof item === "string" && item.trim().length > 0 && item.length <= 160).map((item) => item.trim()))].slice(0, 20),
    catalogOrder: data.catalogOrder === "catalog" ? "catalog" : "newest",
    swatchColors: Object.fromEntries(Object.entries(recordValue(data.swatchColors)).slice(0, 200).filter((entry): entry is [string, string] => entry[0].length <= 160 && typeof entry[1] === "string" && /^#[a-f0-9]{6}$/i.test(entry[1]))),
    labels: Object.fromEntries(Object.entries(recordValue(data.labels)).slice(0, 30).filter(([locale]) => /^[a-z]{2,3}(?:-[A-Za-z0-9]+)*$/.test(locale))
      .map(([locale, labels]) => [locale, Object.fromEntries(Object.entries(recordValue(labels)).slice(0, 200).filter((entry): entry is [string, string] => entry[0].startsWith("module.webshops.") && entry[0].length <= 160 && typeof entry[1] === "string" && entry[1].length <= 500))])),
  };
}
